import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// ─── 401 → silent refresh → retry ────────────────────────────────────
//
// The backend issues short-lived access-tokens (15 min) with a longer
// refresh-token (7 days) — both stored as httpOnly cookies. Prior to
// this interceptor, once the access-token expired every subsequent
// admin XHR came back with 401 not_authenticated (Dashboard would
// stop populating, KYC Reviews / Wallet Portraits / Global Audit /
// Analytics all failed) and the user was forced to log out and back
// in. The interceptor now transparently refreshes the access-token on
// the first 401 and retries the original request exactly once.
//
// Guardrails:
// - We NEVER attempt refresh on the refresh endpoint itself (would
//   loop) or on the auth/me endpoint (that call decides whether
//   we're logged in in the first place).
// - We attempt refresh at most once per underlying request.
// - Concurrent 401s from different pages share a single in-flight
//   refresh so the backend sees one refresh call per expiry.
// - If refresh fails, we let the original 401 bubble so the calling
//   page can render its own empty / error UI, and we broadcast a
//   `veralok:session_expired` DOM event that AuthContext listens to
//   and uses to redirect the user to /login.

let _refreshInFlight = null;

async function _refreshSession() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = api
    .post("/auth/refresh", null, { _skipAuthRetry: true })
    .finally(() => {
      // Clear the shared promise as soon as it settles so a future
      // 401 (e.g. after another 15 min) triggers a fresh refresh.
      _refreshInFlight = null;
    });
  return _refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const url = original.url || "";
    const alreadyRetried = original._retried === true;
    const isRefreshCall = url.includes("/auth/refresh");
    const isMeCall = url.includes("/auth/me") || url.includes("/auth/session");
    const skipRetry = original._skipAuthRetry === true;

    if (status === 401 && !alreadyRetried && !isRefreshCall && !isMeCall && !skipRetry) {
      try {
        await _refreshSession();
        original._retried = true;
        return api(original);
      } catch (_) {
        // Refresh itself failed → session is gone. Emit an event that
        // AuthContext consumes; propagate the original error so the
        // UI still renders its own empty state instead of hanging.
        try {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("veralok:session_expired"));
          }
        } catch (_) {}
      }
    }
    return Promise.reject(error);
  }
);

export function apiErrorText(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail === "object") {
    if (typeof detail.code === "string") return detail.code;
    if (typeof detail.msg === "string") return detail.msg;
    return JSON.stringify(detail);
  }
  return String(detail);
}
